/**
 * Shipping Hooks
 *
 * React Query hooks for shipping tRPC endpoints.
 * FOLLOWS: usePackingMutation.ts patterns
 */

import { trpc } from '@/lib/trpc';
import { skipToken } from '@tanstack/react-query';

/**
 * Query for shipping settings (StarShipIt enabled/disabled).
 * Cached for 5 minutes - rarely changes during a packing session.
 *
 * `orderId` resolves the per-client shipping-provider override (junction
 * `shipping_provider`) for cross-org 3PL orders at a shared location — omit
 * it entirely when not yet known; NEVER pass `''` (the backend schema is
 * `orderId: z.string().uuid().optional()`, which rejects an empty string).
 *
 * Deliberately NO placeholderData/keepPreviousData here. The resolved
 * provider can differ order-to-order at a shared location (client A's
 * orders ship Manual, client B's ship Portless), so carrying the previous
 * order's `shippingSettings` forward while this order's query is still in
 * flight would let a caller render an actionable button for the WRONG
 * provider — a mis-complete hazard. Callers must treat `data === undefined`
 * as "unresolved for this order" and gate accordingly, never coalesce it to
 * a default provider.
 */
export function useShippingSettings(locationId: string | null, orderId?: string | null) {
  return trpc.shipping.getShippingSettings.useQuery(
    locationId ? { locationId, ...(orderId ? { orderId } : {}) } : skipToken,
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    }
  );
}

/**
 * Mutation to send order to StarShipIt.
 * Returns tracking number, carrier, and label PDF.
 * BLU-282: Backend now auto-ships on label generation, so invalidate
 * the same caches as useConfirmShipment.
 */
export function useSendToStarShipIt() {
  const utils = trpc.useUtils();

  return trpc.shipping.sendToStarShipIt.useMutation({
    onSuccess: (_data, variables) => {
      utils.packing.getPackingOrder.invalidate({ orderId: variables.orderId });
      utils.packing.getPackingStats.invalidate();
      utils.packing.getRecentlyPacked.invalidate();
      utils.bins.getBinOccupancy.invalidate();
    },
  });
}

/**
 * Mutation for the provider-agnostic send-to-shipping dispatcher (Phase 51).
 * The backend resolves the location's provider and routes to the matching
 * label-return adapter (StarShipIt | Portless). Same invalidations as
 * useSendToStarShipIt — both auto-ship on success.
 */
export function useSendToShipping() {
  const utils = trpc.useUtils();

  return trpc.shipping.sendToShipping.useMutation({
    onSuccess: (_data, variables) => {
      utils.packing.getPackingOrder.invalidate({ orderId: variables.orderId });
      utils.packing.getPackingStats.invalidate();
      utils.packing.getRecentlyPacked.invalidate();
      utils.bins.getBinOccupancy.invalidate();
    },
  });
}

/**
 * Mutation to re-pull the Portless cross-dock label (printer-jam recovery).
 * READ-ONLY on the backend — no cache invalidations needed.
 */
export function useReprintPortlessLabel() {
  return trpc.shipping.reprintPortlessLabel.useMutation();
}

/**
 * Mutation to fetch an already-bought label again by order, for providers
 * whose registry `reprint` mode is `api` (YunExpress: re-downloads the
 * stored waybill's label). READ-ONLY on the backend — order status and
 * tracking are untouched, so no cache invalidations. A refusal arrives as a
 * PRECONDITION_FAILED whose forwarded `cause.code` is `YUNEXPRESS_NO_WAYBILL`
 * or `YUNEXPRESS_NO_CREDENTIALS`.
 */
export function useReprintShippingLabel() {
  return trpc.shipping.reprintShippingLabel.useMutation();
}

/**
 * Mutation to fetch the 4x6 Hybrid SKU barcode sticker for an order (PR 2).
 * READ-ONLY on the backend (no cache invalidation). Returns `{ isHybrid: false }`
 * for non-hybrid orders; otherwise a base64 PDF the print-agent prints right
 * after the Portless shipping label.
 */
export function useHybridSkuSticker() {
  return trpc.shipping.getHybridSkuSticker.useMutation();
}

/**
 * Mutation to confirm shipment after label printed.
 * Marks order as SHIPPED and releases bin.
 */
export function useConfirmShipment() {
  const utils = trpc.useUtils();

  return trpc.shipping.confirmShipment.useMutation({
    onSuccess: (_data, variables) => {
      utils.packing.getPackingOrder.invalidate({ orderId: variables.orderId });
      utils.packing.getPackingStats.invalidate();
      utils.packing.getRecentlyPacked.invalidate();
      utils.bins.getBinOccupancy.invalidate();
    },
  });
}

/**
 * Mutation for manual complete (no StarShipIt).
 * For locations without StarShipIt integration.
 */
export function useMarkManualComplete() {
  const utils = trpc.useUtils();

  return trpc.shipping.markManualComplete.useMutation({
    onSuccess: (_data, variables) => {
      utils.packing.getPackingOrder.invalidate({ orderId: variables.orderId });
      utils.packing.getPackingStats.invalidate();
      utils.packing.getRecentlyPacked.invalidate();
      utils.bins.getBinOccupancy.invalidate();
    },
  });
}

/**
 * Mutation for the hand over bench: marks a Hand over order as handed to the
 * client (SHIPPED, no label). Idempotent on the backend.
 */
export function useMarkHandedOver() {
  const utils = trpc.useUtils();

  return trpc.shipping.markHandedOver.useMutation({
    onSuccess: (_data, variables) => {
      utils.packing.getPackingOrder.invalidate({ orderId: variables.orderId });
      utils.packing.getPackingStats.invalidate();
      utils.packing.getRecentlyPacked.invalidate();
      utils.bins.getBinOccupancy.invalidate();
    },
  });
}

/**
 * Query for shipping failures (coordinator queue).
 * 30s refetch interval matches issue counts polling.
 */
export function useShippingFailures(locationId: string | null, includeResolved = false) {
  return trpc.shipping.getShippingFailures.useQuery(
    locationId ? { locationId, includeResolved } : skipToken,
    {
      refetchInterval: 30 * 1000, // 30 seconds
      staleTime: 15 * 1000, // 15 seconds
    }
  );
}

/**
 * Mutation to resolve a shipping failure.
 * Invalidates shipping failures query on success.
 */
export function useResolveShippingFailure() {
  const utils = trpc.useUtils();

  return trpc.shipping.resolveShippingFailure.useMutation({
    onSuccess: () => {
      utils.shipping.getShippingFailures.invalidate();
    },
  });
}

/**
 * Result type from sendToShipping/sendToStarShipIt for component state.
 * `trackingNumber` is nullable: Portless cross-dock labels usually carry no
 * tracking at pull time (the customer-facing tracking lands later via
 * Portless → Shopify → order sync).
 */
export interface ShippingResult {
  trackingNumber: string | null;
  carrier: string | null;
  carrierName: string | null;
  labelBase64: string | null;
  labelType: string | null;
}
